import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Download, X, Trophy, Star, CheckCircle2, Loader2, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { api, useAuth } from '../context/AppContext';
import { buildShareUrl } from '../utils/referral';

export default function ChallengeShareModal({ show, onClose, onShared, challengeData, userName }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const { user } = useAuth();

  const totalPoints = challengeData?.total_points_earned || 0;
  const displayName = userName || 'Champion(ne)';
  const shareUrl = buildShareUrl(user?.user_id);

  const persistShare = useCallback(async () => {
    try {
      await api.post('/welcome-challenge/mark-shared');
      if (typeof onShared === 'function') onShared();
    } catch (err) {
      // Non-blocking — share UX should not break if backend hiccups
      console.warn('mark-shared failed', err);
    }
  }, [onShared]);

  const handleShare = useCallback(async (mode = 'share') => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#0a0a0f',
      });

      // Try native share first (mobile) — only when user clicked "Partager"
      if (mode === 'share' && navigator.share && typeof navigator.canShare === 'function') {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], 'yumix-defi-7jours.png', { type: 'image/png' });
          const shareText = `J'ai complete le Defi 7 Jours YUMIX ! ${totalPoints} points gagnes. 🔥\n\nSi tu aimes YUMIX, aide-nous a grandir — rejoins-moi :`;
          const sharePayload = {
            title: 'YUMIX - Defi 7 Jours Complete !',
            text: shareText,
            url: shareUrl,
          };
          // Attach file only if supported (some platforms reject text+url+files combo)
          if (navigator.canShare({ files: [file] })) {
            sharePayload.files = [file];
          }
          if (navigator.canShare(sharePayload)) {
            await navigator.share(sharePayload);
          } else {
            // Last resort: share without files
            await navigator.share({ title: sharePayload.title, text: shareText, url: shareUrl });
          }
          await persistShare();
          toast.success('Partage reussi ! Merci de faire rayonner YUMIX 🙏');
          setGenerating(false);
          if (typeof onClose === 'function') onClose();
          return;
        } catch (shareErr) {
          // User cancelled the native share sheet → don't fall through to download
          if (shareErr && shareErr.name === 'AbortError') {
            setGenerating(false);
            return;
          }
          // Real failure → fall through to download
          console.warn('Native share failed, falling back to download', shareErr);
        }
      }

      // Fallback / explicit download
      const link = document.createElement('a');
      link.download = 'yumix-defi-7jours.png';
      link.href = dataUrl;
      link.click();
      // Also copy the referral URL so the user can paste it next to the image
      try {
        if (navigator.clipboard?.writeText && shareUrl) {
          await navigator.clipboard.writeText(shareUrl);
        }
      } catch { /* clipboard blocked, non-blocking */ }
      await persistShare();
      toast.success('Image sauvegardee + lien copie ! Colle-le sur tes reseaux 🙌');
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      console.error('Share error:', err);
      toast.error('Impossible de generer l\'image. Reessaie dans un instant.');
    } finally {
      setGenerating(false);
    }
  }, [totalPoints, shareUrl, persistShare, onClose]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="challenge-share-modal"
      >
        <motion.div
          initial={{ scale: 0.8, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.8, y: 30 }}
          transition={{ type: 'spring', damping: 15 }}
          className="w-full max-w-sm space-y-4"
          onClick={e => e.stopPropagation()}
        >
          {/* Shareable card */}
          <div
            ref={cardRef}
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, #1a0a00 0%, #0a0a0f 30%, #1a0a00 100%)',
              border: '2px solid rgba(251, 146, 60, 0.4)',
            }}
          >
            {/* Top accent bar */}
            <div style={{
              height: '4px',
              background: 'linear-gradient(90deg, #f97316, #fbbf24, #f97316)',
            }} />

            <div className="p-6 space-y-5">
              {/* Logo + Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <span className="text-sm font-black text-black">Y</span>
                  </div>
                  <span className="font-heading text-sm uppercase tracking-wider text-orange-400">YUMIX</span>
                </div>
                <div className="bg-green-500/15 border border-green-500/30 rounded-full px-3 py-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-[10px] font-bold text-green-400 uppercase">Complete</span>
                </div>
              </div>

              {/* Trophy icon */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(145deg, rgba(251,146,60,0.2) 0%, rgba(251,191,36,0.1) 100%)',
                      boxShadow: '0 0 30px rgba(251,146,60,0.15)',
                    }}
                  >
                    <Trophy className="w-10 h-10 text-amber-400" />
                  </div>
                  {/* Sparkle accents */}
                  <Star className="absolute -top-1 -right-1 w-4 h-4 text-amber-300 animate-pulse" />
                  <Star className="absolute -bottom-0.5 -left-1.5 w-3 h-3 text-orange-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
                </div>
              </div>

              {/* Title */}
              <div className="text-center space-y-1">
                <h2 className="font-heading text-2xl uppercase tracking-tight"
                  style={{
                    background: 'linear-gradient(135deg, #f97316, #fbbf24)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Defi 7 Jours
                </h2>
                <p className="text-lg font-bold text-white uppercase tracking-wide">Complete !</p>
              </div>

              {/* User name */}
              <div className="text-center">
                <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">Athlete</p>
                <p className="text-xl font-heading uppercase tracking-tight text-white">{displayName}</p>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/5">
                  <p className="text-lg font-black text-amber-400">{totalPoints}</p>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Points</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/5">
                  <p className="text-lg font-black text-green-400">7/7</p>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Missions</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/5">
                  <p className="text-lg font-black text-orange-400">100%</p>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Reussite</p>
                </div>
              </div>

              {/* Bottom branding + referral link */}
              <div className="space-y-2 pt-2 border-t border-white/5">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-[10px] text-zinc-600 uppercase tracking-widest">YUMIX</span>
                  <span className="text-zinc-700">|</span>
                  <span className="text-[10px] text-zinc-600 uppercase tracking-widest">Coach YUMIX</span>
                </div>
                {shareUrl && (
                  <p className="text-center text-[9px] text-zinc-500 leading-tight">
                    Rejoins-moi sur YUMIX :
                    <br />
                    <span className="text-amber-400/80 font-mono">{shareUrl.replace(/^https?:\/\//, '')}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Soutien CTA — encourages the user to share for organic growth */}
          <div
            className="flex items-start gap-2 rounded-xl border border-orange-500/20 bg-orange-500/5 px-3 py-2"
            data-testid="challenge-share-cta"
          >
            <Heart className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-zinc-300 leading-snug">
              Si tu aimes YUMIX, partage ce lien pour aider l'app a grandir.
              Chaque ami invite te fait gagner des points Eclaireur 🚀
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => handleShare('share')}
              disabled={generating}
              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-black font-heading uppercase text-sm hover:from-orange-600 hover:to-amber-600"
              data-testid="share-challenge-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <Share2 className="w-4 h-4 mr-1.5" />
              )}
              Partager
            </Button>
            <Button
              onClick={() => handleShare('download')}
              disabled={generating}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-heading uppercase text-sm"
              data-testid="download-challenge-btn"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Sauvegarder
            </Button>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            className="w-full text-center text-xs text-zinc-500 hover:text-zinc-300 transition-colors py-2"
            data-testid="close-challenge-share"
          >
            Fermer
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
